import { defineComponent as _defineComponent } from 'vue'
import TableWithActions2 from '@/components/tableWithActions/TableWithActions2.vue';
import CellWithSubtitle from '@/components/tableWithActions/CellWithSubtitle.vue';
import TableColumnActionsWrapper from '@/components/tableWithActions/TableColumnActionsWrapper.vue';
import TableActionsList from '@/components/tableWithActions/TableActionsList.vue';
import TableActionButton2 from '@/components/tableWithActions/TableActionButton2.vue';
import BendProductionHistoryFilters from '@/components/common/BendProductionHistoryFilters.vue';
import BendingDetailPlanModal from '@/views/workcenterConsole/BendIntradayAvailabilityTable/BendingPlanDetailModal.vue';
import {
  BendProductionHistoryData,
  BendProductionHistoryPlan,
} from '@/models/Charts/chartsData';
import { devicesService } from '@/services/devices.service';
import { Device } from '@/models/device';
import { computed, getCurrentInstance, ref, shallowRef } from 'vue';
import { useI18n } from '@/composables/i18n';
import {
  lengthUnit,
  weightUnit,
  millimetersToCustomerUnit,
  kilogramsToCustomerUnit,
} from '@/utils/measurement';
import { formatDuration } from '@/utils/dates';
import moment from 'moment';
import {
  abbreviateNumber,
  formatPercentage,
  formatPercentageOrDefault,
} from '@/utils/number';
import { modalService } from '@/library-services/modal.service';
import { isEmpty, isNil, valueOrDefault } from '@/utils/misc';
import {
  createTextListMatcher,
  createTextListMatcherForUiNumber,
  createTextListMatcherForNumber,
  FilterMatcher,
} from '@/utils/table';
import { BendProductionHistoryFilterValues } from './BendProductionHistoryFilterValues';


export default /*#__PURE__*/_defineComponent({
  __name: 'BendProductionHistory',
  props: {
    data: null,
    loading: { type: Boolean }
  },
  setup(__props: any) {

const props = __props as {
  data: BendProductionHistoryData[] | null;
  loading: boolean;
};



const app = getCurrentInstance();
const { t } = useI18n();

const areApplyingFilters = ref(false);

const filterValues = shallowRef<BendProductionHistoryFilterValues>({
  partName: [],
  partDescription: [],
  partOriginator: [],
  material: [],
  thicknessMm: [],
  bendsPerPart: [],
  partWeightKg: [null, null],
  users: [],
});

function getDeviceByStringId(deviceStringId: string): Device {
  return devicesService.store.findByStringId(deviceStringId)!;
}

function getDeviceNameByStringId(deviceStringId: string): string {
  return getDeviceByStringId(deviceStringId).name;
}

function getDeviceModelNameByStringId(deviceStringId: string): string {
  return getDeviceByStringId(deviceStringId).deviceBaseModelName;
}

function formatDateTime(date: Date): string {
  return moment(date).format('YYYY-MM-DD, HH:mm:ss');
}

function getDateTimeDiffSeconds(
  startDateTime: Date,
  endDateTime: Date,
): number {
  return moment(endDateTime).diff(startDateTime) / 1000;
}

function formatPlanDuration(plan: BendProductionHistoryPlan) {
  return formatDuration(
    getDateTimeDiffSeconds(plan.startDateTime, plan.endDateTime),
  );
}

function sum(list: number[]) {
  return list.reduce((a, b) => a + b, 0);
}

function getTotalTimeForDeviceById(deviceStringId: string): string {
  const duration = sum(
    filteredData.value
      .find((x) => x.deviceStringId === deviceStringId)!
      .plans.map((x) => getDateTimeDiffSeconds(x.startDateTime, x.endDateTime)),
  );
  return formatDuration(duration);
}

function getTotalPartCountForDeviceById(deviceStringId: string): number {
  return sum(
    filteredData.value
      .find((x) => x.deviceStringId === deviceStringId)!
      .plans.map((x) => x.partsCount ?? 0),
  );
}

function getTotalBendCountForDeviceById(deviceStringId: string): number {
  return sum(
    filteredData.value
      .find((x) => x.deviceStringId === deviceStringId)!
      .plans.map((x) => x.bendsCount),
  );
}

function getFormattedTotalProductivityForDeviceById(
  deviceStringId: string,
): string {
  return getFormattedTotalPercentageFieldForDeviceById(
    deviceStringId,
    'productivity',
  );
}

function getFormattedTotalPerformanceForDeviceById(
  deviceStringId: string,
): string {
  return getFormattedTotalPercentageFieldForDeviceById(
    deviceStringId,
    'performance',
  );
}

function getFormattedTotalPercentageFieldForDeviceById(
  deviceStringId: string,
  fieldName: keyof BendProductionHistoryPlan,
): string {
  const nonNilProductivityValues = filteredData.value
    .find((deviceEntry) => deviceEntry.deviceStringId === deviceStringId)!
    .plans.filter((plan) => !isNil(plan[fieldName]))
    .map((plan) => plan[fieldName]! as number);

  if (isEmpty(nonNilProductivityValues)) {
    return '-';
  }

  return formatPercentage(
    sum(nonNilProductivityValues) / nonNilProductivityValues.length,
  );
}

function openProductionPlanModal(
  plan: BendProductionHistoryPlan,
  deviceStringId: string,
): void {
  modalService.compositionApi.open(
    {
      component: BendingDetailPlanModal,
      props: {
        partGuid: plan.partGuid,
        intervalId: plan.intervalId,
        deviceStringId,
      },
    },
    app,
  );
}

function onFiltersChanged(
  newFilters: BendProductionHistoryFilterValues,
  areApplying: boolean,
  next: (filterValues: Record<string, any>, areApplying: boolean) => void,
) {
  filterValues.value = newFilters;
  areApplyingFilters.value = areApplying;
  next({}, areApplying);
}

function weightMatcher(
  plan: BendProductionHistoryPlan,
  filterValue: [number | null, number | null],
): boolean {
  const planPartWeightInCustomerUnit = kilogramsToCustomerUnit(
    plan.partWeightKg ?? 0,
  );
  const minWeightInCustomerUnit = filterValue[0] ?? 0;
  const maxWeightInCustomerUnit = filterValue[1] ?? Number.MAX_VALUE;

  return (
    planPartWeightInCustomerUnit >= minWeightInCustomerUnit &&
    planPartWeightInCustomerUnit <= maxWeightInCustomerUnit
  );
}

const filterMatchers = computed<
  Record<keyof BendProductionHistoryFilterValues, FilterMatcher>
>(() => ({
  partName: createTextListMatcher('partName'),
  partDescription: createTextListMatcher('partDescription'),
  partOriginator: createTextListMatcher('partOriginator'),
  material: createTextListMatcher('material'),
  thicknessMm: createTextListMatcherForUiNumber('thicknessMm', adaptThickness),
  bendsPerPart: createTextListMatcherForNumber('bendsPerPart'),
  partWeightKg: weightMatcher,
  users: createTextListMatcher('users'),
}));

function adaptThickness(thicknessMm: number | null): string {
  if (isNil(thicknessMm)) {
    return '-';
  }
  return abbreviateNumber(millimetersToCustomerUnit(thicknessMm));
}

function adaptWeight(weightKg: number | null): string {
  if (isNil(weightKg)) {
    return '-';
  }
  return abbreviateNumber(kilogramsToCustomerUnit(weightKg));
}

const filteredData = computed<BendProductionHistoryData[]>(() =>
  (props.data ?? [])
    .map((dataItem) => ({
      ...dataItem,
      plans: dataItem.plans.filter((planItem) =>
        Object.entries(filterValues.value).every(([filterKey, filterValue]) => {
          const matches =
            filterMatchers.value[
              filterKey as keyof BendProductionHistoryFilterValues
            ];
          return matches?.(planItem, filterValue);
        }),
      ),
    }))
    .filter((item) => !isEmpty(item.plans) || !areApplyingFilters.value),
);

return { __sfc: true,props, app, t, areApplyingFilters, filterValues, getDeviceByStringId, getDeviceNameByStringId, getDeviceModelNameByStringId, formatDateTime, getDateTimeDiffSeconds, formatPlanDuration, sum, getTotalTimeForDeviceById, getTotalPartCountForDeviceById, getTotalBendCountForDeviceById, getFormattedTotalProductivityForDeviceById, getFormattedTotalPerformanceForDeviceById, getFormattedTotalPercentageFieldForDeviceById, openProductionPlanModal, onFiltersChanged, weightMatcher, filterMatchers, adaptThickness, adaptWeight, filteredData, TableWithActions2, CellWithSubtitle, TableColumnActionsWrapper, TableActionsList, TableActionButton2, BendProductionHistoryFilters, lengthUnit, weightUnit, abbreviateNumber, formatPercentageOrDefault, isEmpty, isNil, valueOrDefault }
}

})